import { z } from "zod";
import { SystemEvent, SystemEntity } from "@erp/shared-types";
import { entityOfType, SystemEntitySchema } from "../utils/system-entities";

// ============================================================================
// Event Schemas
// ============================================================================

/**
 * Schema for EMAIL_INGESTED event
 * Emitted after an email is ingested and OCR has been performed
 */
export const EmailIngestedEventSchema = z.object({
  inboundEmailId: z.uuid(),
  organizationId: z.uuid(),
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  from: z.string().optional(),
  subject: z.string().optional(),
  messageId: z.string(),
  conversationId: z.string().optional(),
  documentIds: z.array(z.uuid()),
  mimeTypes: z.array(z.string()),
  attachmentCount: z.number(),
  classificationHints: z
    .array(z.string())
    .optional()
    .describe(
      "Hints about what workflows might match (e.g., 'ap_invoice', 'purchase_order')",
    ),
  routerHints: z
    .object({
      ap: z.boolean().optional(),
      po: z.boolean().optional(),
      quoteSheet: z.boolean().optional(),
    })
    .optional(),
});

export type EmailIngestedEvent = z.infer<typeof EmailIngestedEventSchema>;

/**
 * Schema for EMAIL_ROUTED_EMAIL_RULE event
 * Emitted after router determines email should be processed as email rule
 */
export const EmailRoutedEmailRuleEventSchema = z.object({
  emailRuleOptionId: z.uuid().optional(),
  emailRuleId: z.uuid().optional(),
  emailRuleIds: z.array(z.uuid()).optional(),
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
  /** Documents (attachments + email body) associated with the inbound email */
  documents: z.array(entityOfType(SystemEntity.CORE_DOCUMENT)).optional(),
});

export type EmailRoutedEmailRuleEvent = z.infer<
  typeof EmailRoutedEmailRuleEventSchema
>;
/**
 * Schema for EMAIL_ROUTED_AR_REMITTANCE event
 * Emitted after router determines email should be processed as AR remittance
 */
export const EmailRoutedARRemittanceEventSchema = z.object({
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
});

export type EmailRoutedARRemittanceEvent = z.infer<
  typeof EmailRoutedARRemittanceEventSchema
>;

export const APInvoiceAcceptedEventSchema = z.object({
  apInvoice: entityOfType(SystemEntity.AP_INVOICE),
});

export type APInvoiceAcceptedEvent = z.infer<
  typeof APInvoiceAcceptedEventSchema
>;

/**
 * Schema for SALES_ORDER_APPROVED event
 * Emitted when a sales order draft is approved and synced to ERP
 */
export const SalesOrderApprovedEventSchema = z.object({
  salesOrder: entityOfType(SystemEntity.AR_SALES_ORDER),
  salesOrderDraft: entityOfType(SystemEntity.AR_STAGED_SALES_ORDER),
  organizationId: z.uuid(),
  /** Flat ID for workflow edge wiring (duplicates salesOrder.entityId). */
  salesOrderId: z.uuid(),
  /** Flat ID for workflow edge wiring (duplicates salesOrderDraft.entityId). */
  salesOrderDraftId: z.uuid(),
  /** Customer ID for resolving customer-scoped watchers in notification workflows. */
  customerId: z.uuid().nullable(),
  /** Serialised approval issues (may be empty when no issues). */
  issues: z.array(
    z.object({
      lineItemIndex: z.number(),
      issue: z.object({
        type: z.string(),
        details: z.string().optional(),
        excludedFromOrder: z.boolean(),
      }),
    }),
  ),
});

export type SalesOrderApprovedEvent = z.infer<
  typeof SalesOrderApprovedEventSchema
>;

/**
 * Schema for DRAFT_PURCHASE_ORDER_CREATED event (procurement PO approval).
 * Emitted when buyer Confirm stages a draft purchase order held for approval.
 * `divisionId` routes to the correct per-division approval workflow.
 */
export const DraftPurchaseOrderCreatedEventSchema = z.object({
  draftPurchaseOrderId: z.uuid(),
  organizationId: z.uuid(),
  /** Division of the PO's ship-to warehouse; the per-division trigger filters on it. */
  divisionId: z.uuid().nullable(),
});

export type DraftPurchaseOrderCreatedEvent = z.infer<
  typeof DraftPurchaseOrderCreatedEventSchema
>;

/**
 * Schema for TASK_CREATED event
 * Emitted when a new task is created via createTask or generateTask
 */
export const TaskCreatedEventSchema = z.object({
  task: entityOfType(SystemEntity.TASK),
  organizationId: z.uuid(),
  sourceEntity: SystemEntitySchema.optional(),
});

export type TaskCreatedEvent = z.infer<typeof TaskCreatedEventSchema>;

/**
 * Schema for REBATE_CSV_TRANSFORM_STARTED event
 * Only passes sessionId — the auto-transform handler loads everything else from the session.
 */
export const RebateCsvTransformStartedEventSchema = z.object({
  rebateSessionId: z.string().uuid(),
});

export type RebateCsvTransformStartedEvent = z.infer<
  typeof RebateCsvTransformStartedEventSchema
>;

/**
 * Shared input shape for SPA change-request workflow steps (fetch vendor SPA,
 * fetch AS400 current loads, calculate price changes). Carries the SPA (quote)
 * number and vendor key so each step can route to the correct vendor portal
 * without re-fetching. The workflow is triggered by `SPA_REQUEST_SUBMITTED`,
 * whose payload supplies these fields to the trigger's output sockets.
 */
export const SpaWorkflowInputSchema = z.object({
  spaNumber: z.string().min(1),
  vendor: z.string().min(1),
});

export type SpaWorkflowInput = z.infer<typeof SpaWorkflowInputSchema>;

/**
 * Schema for SPA_REQUEST_SUBMITTED event
 * Emitted when a SpaRequest entity is created in the DB (Module 1 create handler).
 * This is the event the Module 9 DAG listens on to start the full SPA pipeline.
 */
export const SpaRequestSubmittedEventSchema = z.object({
  spaRequestId: z.string().uuid(),
  spaNumber: z.string().min(1),
  vendor: z.string().min(1),
  organizationId: z.string().uuid(),
});

export type SpaRequestSubmittedEvent = z.infer<
  typeof SpaRequestSubmittedEventSchema
>;

/**
 * Schema for APPROVAL_NEEDED event
 * Emitted when a staged PO requires approval — either discount-level (unapproved pricing)
 * or order-level (e.g., total exceeds threshold). Triggers the notification workflow.
 */
export const ApprovalNeededEventSchema = z.object({
  organizationId: z.uuid(),
  /** The staged purchase order requiring approval */
  stagedPurchaseOrderId: z.uuid(),
  /** Customer ID for resolving customer-scoped approvers */
  customerId: z.uuid().nullable(),
  /**
   * The discount approval level number that needs to act next. Set when
   * the next pending action is a discount approval, null otherwise.
   * Exactly one of nextDiscountApprovalLevel / nextOrderApprovalLevel is
   * non-null per event.
   */
  nextDiscountApprovalLevel: z.number().int().positive().nullable(),
  /** The order approval level number that needs to act next, or null. */
  nextOrderApprovalLevel: z.number().int().positive().nullable(),
  /** Idempotency key prefix for email deduplication */
  idempotencyKeyPrefix: z.string(),
  /** User IDs to exclude from notification (e.g., the user who just triggered the recheck) */
  excludeUserIds: z.array(z.string().uuid()).nullable(),
  /**
   * Full order results with issue details for rendering notification email.
   * Same shape as detect_order_issues output.
   */
  orderResults: z.array(
    z.object({
      stagedPurchaseOrderId: z.uuid(),
      customerId: z.uuid().nullable(),
      issues: z.array(
        z.object({
          type: z.string(),
          description: z.string(),
          lineItemIndex: z.number().optional(),
          lineItemDescription: z.string().optional(),
        }),
      ),
      issueTypes: z.array(z.string()),
      orderSummary: z.object({
        purchaseOrderNumber: z.string().nullable(),
        customerName: z.string().nullable(),
        customerNumber: z.string().nullable().optional(),
        orderType: z.string().nullable().optional(),
        shipToLocation: z.string().nullable().optional(),
        orderDate: z.string().nullable().optional(),
        requestDate: z.string().nullable().optional(),
        totalAmount: z.number().nullable(),
        currency: z.string().nullable(),
        lineItemCount: z.number(),
        receivedDate: z.string().nullable(),
      }),
    }),
  ),
});

export type ApprovalNeededEvent = z.infer<typeof ApprovalNeededEventSchema>;

/**
 * Schema for TASK_COMPLETED event (counterpart to TASK_CREATED).
 * Emitted when any task is completed. Carries the task's category + linked
 * entity + result so a workflow can re-trigger off it and filter to the tasks
 * it cares about (e.g. OMS v3 workflow-3 filters to category "sales_order").
 * Single re-trigger event for both approval decisions and corrective actions.
 */
export const TaskCompletedEventSchema = z.object({
  /** The formal Task entity, when one exists (omitted for task-less approvals). */
  task: entityOfType(SystemEntity.TASK).optional(),
  taskId: z.uuid().optional(),
  organizationId: z.uuid(),
  /** Task category used by event-trigger payload filters (e.g. "sales_order"). */
  category: z.string().optional(),
  /** The entity the task is about (e.g. the draft sales order). */
  linkedEntity: SystemEntitySchema.optional(),
  /** Flat draft id for workflow edge wiring, when the task is draft-scoped. */
  draftSalesOrderId: z.uuid().optional(),
  /** Flat discount-group id, when the task is a Discounts v2 group approval. */
  discountGroupId: z.uuid().optional(),
  /** Flat draft-PO id, when the task is a procurement PO approval. */
  draftPurchaseOrderId: z.uuid().optional(),
  /** Outcome of the task (e.g. approval decision: approved | rejected). */
  decision: z.string().optional(),
});

export type TaskCompletedEvent = z.infer<typeof TaskCompletedEventSchema>;

/**
 * Schema for DRAFT_SALES_ORDER_ROUTED event (OMS v3, routing org → target org).
 * Cross-org: carries the target organization the routing workflow classified.
 */
export const DraftSalesOrderRoutedEventSchema = z.object({
  draftSalesOrderId: z.uuid(),
  targetOrganizationId: z.uuid(),
  inboundEmailId: z.uuid().optional(),
});

export type DraftSalesOrderRoutedEvent = z.infer<
  typeof DraftSalesOrderRoutedEventSchema
>;

/**
 * Schema for DRAFT_SALES_ORDER_READY event (OMS v3, intra-org wf2 → wf3).
 * Emitted when the target org's extract/line-breakout workflow has populated
 * the draft and it is ready for the approval-chain workflow.
 */
export const DraftSalesOrderReadyEventSchema = z.object({
  draftSalesOrderId: z.uuid(),
});

export type DraftSalesOrderReadyEvent = z.infer<
  typeof DraftSalesOrderReadyEventSchema
>;

/**
 * Schema for DRAFT_SALES_ORDER_UNCLASSIFIED event (OMS v3, routing org).
 * Emitted when classify_organization can't confidently match a target org, so an
 * admin-notification workflow can alert the Gently team to assign it manually.
 */
export const DraftSalesOrderUnclassifiedEventSchema = z.object({
  draftSalesOrderId: z.uuid(),
});

export type DraftSalesOrderUnclassifiedEvent = z.infer<
  typeof DraftSalesOrderUnclassifiedEventSchema
>;

/**
 * Schema for DISCOUNT_GROUP_SUBMITTED event (Discounts v2).
 * Emitted when a discount group (exception price list / bulk batch / discount
 * request) is submitted for approval; triggers the group-approval workflow.
 */
export const DiscountGroupSubmittedEventSchema = z.object({
  organizationId: z.uuid(),
  discountGroupId: z.uuid(),
});

export type DiscountGroupSubmittedEvent = z.infer<
  typeof DiscountGroupSubmittedEventSchema
>;

/**
 * Schema for DISCOUNT_REQUEST_DOCUMENT_ROUTED event (Discounts v2, routing org
 * → target org). Cross-org: the routing workflow classified an inbound email's
 * document(s) as a Discount Request and matched the selling org. Carries the
 * inbound email so the target org's discount-request-ingestion workflow can
 * extract its discount request attachment(s) and create the discount group
 * there.
 */
export const DiscountRequestDocumentRoutedEventSchema = z.object({
  /** Plain id, for uuid inputs (e.g. custom canvas wiring). */
  inboundEmailId: z.uuid(),
  /**
   * The same email as an entity reference — this is what Extract Document
   * Content (V2)'s `inboundEmail` input expects, so the trigger→extract edge
   * type-checks on the canvas (uuid → entity_reference is rejected there,
   * even though the runtime schema would coerce a bare uuid).
   */
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
  targetOrganizationId: z.uuid(),
});

export type DiscountRequestDocumentRoutedEvent = z.infer<
  typeof DiscountRequestDocumentRoutedEventSchema
>;

/**
 * Schema for SLACK_WEBHOOK_RECEIVED event
 * Emitted by the Slack webhook receiver for Events API callbacks,
 * interactivity payloads (button clicks, modal submissions), and slash
 * commands. Common identifiers are flattened for easy wiring in workflow
 * steps; the full raw Slack payload is preserved in `payload`.
 */
export const SlackWebhookReceivedEventSchema = z.object({
  /** Which Slack delivery mechanism produced this event */
  kind: z.enum(["event", "interaction", "command"]),
  // The flattened fields below are ALWAYS present — the receiver defaults
  // fields that don't apply to a delivery kind to "". Event-trigger output
  // sockets are required, and a missing value would leave downstream steps
  // (e.g. a Decision on `command`) permanently pending.
  /** Slack workspace (team) id, e.g. T08DR30P6KA */
  teamId: z.string(),
  /** Slack user id of the actor, e.g. U012AB3CD */
  slackUserId: z.string(),
  /** Slack channel / conversation id the activity occurred in */
  channelId: z.string(),
  /** Inner event type for Events API callbacks (e.g. "message"); "" otherwise */
  eventType: z.string(),
  /** Message text for message events, or command text for slash commands */
  text: z.string(),
  /** action_id of the clicked block element for interactions; "" otherwise */
  actionId: z.string(),
  /** value of the clicked block element for interactions; "" otherwise */
  actionValue: z.string(),
  /** Slash command name including the leading slash (e.g. "/register"); "" otherwise */
  command: z.string(),
  /** Short-lived URL for responding to interactions / slash commands; "" otherwise */
  responseUrl: z.string(),
  /** Short-lived trigger id usable for views.open within 3 seconds; "" otherwise */
  triggerId: z.string(),
  /** Full raw Slack payload as received (after JSON / form decoding) */
  payload: z.record(z.string(), z.unknown()),
});

export type SlackWebhookReceivedEvent = z.infer<
  typeof SlackWebhookReceivedEventSchema
>;

// Map event types to their data types (pre-computed to avoid deep instantiation)
export type SystemEventData = {
  [SystemEvent.EMAIL_INGESTED]: EmailIngestedEvent;
  [SystemEvent.EMAIL_ROUTED_EMAIL_RULE]: EmailRoutedEmailRuleEvent;
  [SystemEvent.EMAIL_ROUTED_AR_REMITTANCE]: EmailRoutedARRemittanceEvent;
  [SystemEvent.AP_INVOICE_ACCEPTED]: APInvoiceAcceptedEvent;
  [SystemEvent.SALES_ORDER_APPROVED]: SalesOrderApprovedEvent;
  [SystemEvent.DRAFT_PURCHASE_ORDER_CREATED]: DraftPurchaseOrderCreatedEvent;
  [SystemEvent.TASK_CREATED]: TaskCreatedEvent;
  [SystemEvent.TASK_COMPLETED]: TaskCompletedEvent;
  [SystemEvent.REBATE_CSV_TRANSFORM_STARTED]: RebateCsvTransformStartedEvent;
  [SystemEvent.SPA_REQUEST_SUBMITTED]: SpaRequestSubmittedEvent;
  [SystemEvent.APPROVAL_NEEDED]: ApprovalNeededEvent;
  [SystemEvent.DRAFT_SALES_ORDER_ROUTED]: DraftSalesOrderRoutedEvent;
  [SystemEvent.DRAFT_SALES_ORDER_READY]: DraftSalesOrderReadyEvent;
  [SystemEvent.DRAFT_SALES_ORDER_UNCLASSIFIED]: DraftSalesOrderUnclassifiedEvent;
  [SystemEvent.DISCOUNT_GROUP_SUBMITTED]: DiscountGroupSubmittedEvent;
  [SystemEvent.DISCOUNT_REQUEST_DOCUMENT_ROUTED]: DiscountRequestDocumentRoutedEvent;
  [SystemEvent.SLACK_WEBHOOK_RECEIVED]: SlackWebhookReceivedEvent;
};

export const SystemEventMap = {
  [SystemEvent.EMAIL_INGESTED]: EmailIngestedEventSchema,
  [SystemEvent.EMAIL_ROUTED_EMAIL_RULE]: EmailRoutedEmailRuleEventSchema,
  [SystemEvent.EMAIL_ROUTED_AR_REMITTANCE]: EmailRoutedARRemittanceEventSchema,
  [SystemEvent.AP_INVOICE_ACCEPTED]: APInvoiceAcceptedEventSchema,
  [SystemEvent.SALES_ORDER_APPROVED]: SalesOrderApprovedEventSchema,
  [SystemEvent.DRAFT_PURCHASE_ORDER_CREATED]:
    DraftPurchaseOrderCreatedEventSchema,
  [SystemEvent.TASK_CREATED]: TaskCreatedEventSchema,
  [SystemEvent.TASK_COMPLETED]: TaskCompletedEventSchema,
  [SystemEvent.REBATE_CSV_TRANSFORM_STARTED]:
    RebateCsvTransformStartedEventSchema,
  [SystemEvent.SPA_REQUEST_SUBMITTED]: SpaRequestSubmittedEventSchema,
  [SystemEvent.APPROVAL_NEEDED]: ApprovalNeededEventSchema,
  [SystemEvent.DRAFT_SALES_ORDER_ROUTED]: DraftSalesOrderRoutedEventSchema,
  [SystemEvent.DRAFT_SALES_ORDER_READY]: DraftSalesOrderReadyEventSchema,
  [SystemEvent.DRAFT_SALES_ORDER_UNCLASSIFIED]:
    DraftSalesOrderUnclassifiedEventSchema,
  [SystemEvent.DISCOUNT_GROUP_SUBMITTED]: DiscountGroupSubmittedEventSchema,
  [SystemEvent.DISCOUNT_REQUEST_DOCUMENT_ROUTED]:
    DiscountRequestDocumentRoutedEventSchema,
  [SystemEvent.SLACK_WEBHOOK_RECEIVED]: SlackWebhookReceivedEventSchema,
} satisfies Record<SystemEvent, z.ZodType>;

export type SystemEventMapType = typeof SystemEventMap;
